import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, LoadingBlock, TextInput } from '../components/ui.tsx';
import { ProviderMark } from '../components/social-buttons.tsx';
import { useAppConfig } from '../components/turnstile.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import AuthShell from '../components/auth-shell.tsx';
import { browserTimezone } from '../forms/timezones.ts';
import { activeLanguage } from '../i18n/index.ts';
import ServiceModeChoice from '../components/service-mode-choice.tsx';
import { OAUTH_PROVIDER_LABELS, type ServiceMode } from '@shared/constants/index.ts';
import { isAcceptableSignupPhone } from '@shared/signup-phone.ts';
import type { HeardAboutChoice } from '@shared/heard-about.ts';
import HeardAboutField from '../components/heard-about-field.tsx';
import './auth-read-state.css';

/**
 * The last step of signing up with Google, Apple, Facebook or Microsoft.
 *
 * Not reachable from X, Instagram or TikTok: those three share no email address,
 * and a tenant cannot be created without one. They connect from Settings
 * instead — see services/oauth/providers.ts.
 *
 * The provider has already told us who this is; what it cannot tell us is the
 * name of the company being created, and a tenant has no meaning without one.
 * So the only things asked for here are the two a provider cannot supply and an
 * account cannot be created without: that name, and whether the company cleans
 * homes, businesses or both — which the password signup form asks too, because
 * it decides which starter library the account is built with.
 *
 * The identity itself is not in this page's hands — it is in a signed, HttpOnly
 * cookie the Worker set on the way back from the provider, which this page can
 * neither read nor forge. If it has expired there is nothing to finish, and the
 * page says so rather than showing a form that cannot submit.
 */
export default function CompleteSignupPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('completeSignup.documentTitle'));
  const navigate = useNavigate();
  const { setSession } = useSession();
  const config = useAppConfig();
  const trialDays = config.data?.trialDays ?? 14;

  const pending = useQuery({
    queryKey: ['oauth-pending-signup'],
    queryFn: () => authApi.pendingOauthSignup(),
    retry: false,
    // Single-use, and spent by the submission below: a refetch would only ever
    // find it gone.
    staleTime: Infinity,
  });

  const [companyName, setCompanyName] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  // Unanswered until somebody answers it — see the register page.
  const [serviceMode, setServiceMode] = useState<ServiceMode | null>(null);
  const [heardAbout, setHeardAbout] = useState<HeardAboutChoice | ''>('');
  const [heardAboutDetail, setHeardAboutDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // The provider's own name for the person, as a starting point they can edit.
  useEffect(() => {
    if (pending.data?.fullName && !fullName) setFullName(pending.data.fullName);
  }, [pending.data?.fullName, fullName]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    // The API leaves this optional so an older client can still finish a
    // signup; this form is what insists on an answer.
    if (!serviceMode) {
      setError(null);
      setFieldErrors({ serviceMode: t('completeSignup.serviceModeRequired') });
      return;
    }

    if (phone.trim() && !isAcceptableSignupPhone(phone)) {
      setError(null);
      setFieldErrors({ phone: t('completeSignup.phoneInvalid') });
      return;
    }

    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      const timezone = browserTimezone();
      const session = await authApi.completeOauthSignup({
        companyName,
        fullName,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(timezone ? { timezone } : {}),
        // Same starter data as a password signup, so the same language.
        language: activeLanguage(),
        serviceMode,
        ...(heardAbout ? { heardAbout } : {}),
        ...(heardAbout === 'other' && heardAboutDetail.trim() ? { heardAboutDetail: heardAboutDetail.trim() } : {}),
      });
      setSession(session);
      // Where the deployment mandates a second factor, a brand-new account
      // owes one from the start — the same landing the sign-in paths use.
      navigate(session.mfaSetupRequired ? '/settings/security' : '/', {
        replace: true,
      });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fields);
      } else {
        setError(t('completeSignup.networkError'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (pending.isLoading) {
    return (
      <div className="auth-signup-state">
        <AuthShell title={t('completeSignup.title')}>
          <LoadingBlock label={t('completeSignup.checkingSignIn')} />
        </AuthShell>
      </div>
    );
  }

  if (pending.isError || !pending.data) {
    return (
      <div className="auth-signup-state">
        <AuthShell title={t('completeSignup.title')}>
          <Alert tone="error" title={t('completeSignup.expiredTitle')}>
            {t('completeSignup.expiredMessage')}
          </Alert>
          <p className="mt-6 text-sm">
            <Link to="/register" className="text-[color:var(--color-brand)] hover:underline">
              {t('completeSignup.backToSignUp')}
            </Link>
          </p>
        </AuthShell>
      </div>
    );
  }

  const provider = pending.data.provider;

  return (
    <AuthShell
      title={t('completeSignup.oneMoreThing')}
      wide
      subtitle={t('completeSignup.subtitle', { provider: OAUTH_PROVIDER_LABELS[provider], trialDays })}
    >
      <div className="mb-6 flex items-center gap-3 rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3">
        <ProviderMark provider={provider} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{pending.data.email}</p>
          <p className="text-xs text-[color:var(--color-muted)]">
            {t('completeSignup.signingUpWith', { provider: OAUTH_PROVIDER_LABELS[provider] })}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        <TextInput
          label={t('completeSignup.companyNameLabel')}
          name="companyName"
          autoComplete="organization"
          required
          value={companyName}
          error={fieldErrors.companyName}
          onChange={(event) => setCompanyName(event.target.value)}
        />
        <TextInput
          label={t('completeSignup.yourNameLabel')}
          name="fullName"
          autoComplete="name"
          required
          value={fullName}
          error={fieldErrors.fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
        <TextInput
          label={t('completeSignup.phoneLabel')}
          type="tel"
          name="phone"
          autoComplete="tel"
          hint={t('completeSignup.phoneHint')}
          value={phone}
          error={fieldErrors.phone}
          onChange={(event) => setPhone(event.target.value)}
        />

        <ServiceModeChoice
          name="complete-signup-service-mode"
          value={serviceMode}
          onChange={(mode) => {
            setServiceMode(mode);
            setFieldErrors((current) => {
              const next = { ...current };
              delete next.serviceMode;
              return next;
            });
          }}
          hint={t('completeSignup.serviceModeHint')}
          error={fieldErrors.serviceMode}
        />

        <HeardAboutField
          choice={heardAbout}
          detail={heardAboutDetail}
          onChoice={setHeardAbout}
          onDetail={setHeardAboutDetail}
        />

        <Button type="submit" variant="primary" busy={busy} className="w-full">
          {t('completeSignup.createCompany')}
        </Button>
      </form>

      <p className="mt-6 text-sm text-[color:var(--color-muted)]">{t('completeSignup.addPasswordLaterNote')}</p>
    </AuthShell>
  );
}
